import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import SuperAdminLayout from "./SuperAdminLayout";
import {
    fetchTenants, createTenant, suspendTenant, reactivateTenant,
    deleteTenant, impersonateTenant, changeTenantPlan, fetchPlans
} from "@/services/superadminApi";
import {
    Search, Filter, Store, ChevronRight, Globe, Calendar,
    CreditCard, Plus, Pause, PlayCircle, Trash2, LogIn, X,
    AlertTriangle, RefreshCw, Building2
} from 'lucide-react';

// ── Sub-componentes internos ──────────────────────────────────────────────────

function SkeletonCard() {
    return (
        <div className="sa-card animate-pulse">
            <div className="flex gap-4 items-center mb-6">
                <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(255,255,255,0.06)' }} />
                <div className="flex-1 space-y-2">
                    <div style={{ height: 16, width: '60%', borderRadius: 8, background: 'rgba(255,255,255,0.06)' }} />
                    <div style={{ height: 12, width: '40%', borderRadius: 8, background: 'rgba(255,255,255,0.04)' }} />
                </div>
            </div>
            <div style={{ height: 80, borderRadius: 12, background: 'rgba(255,255,255,0.04)', marginBottom: 16 }} />
            <div style={{ height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.04)' }} />
        </div>
    );
}

function EmptyState({ onClear, search }) {
    return (
        <div className="col-span-full sa-empty-state">
            <Building2 size={52} className="sa-empty-icon" />
            {search
                ? <p className="sa-empty-title">Sin resultados para "{search}"</p>
                : <p className="sa-empty-title">Aún no hay restaurantes registrados.</p>
            }
            <p className="sa-empty-sub">
                {search ? "Prueba otro término de búsqueda o limpia los filtros." : "Crea el primer restaurante usando el botón superior."}
            </p>
            {search && (
                <button className="sa-btn-secondary" onClick={onClear}>
                    Limpiar filtros
                </button>
            )}
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        active: { label: "Activo", class: "badge-success" },
        trial: { label: "Trial", class: "badge-info" },
        paused: { label: "Pausado", class: "badge-warning" },
        suspended: { label: "Suspendido", class: "badge-danger" },
        cancelled: { label: "Cancelado", class: "badge-danger" },
    };
    const s = map[status] || { label: status || "Sin plan", class: "badge-muted" };
    return <span className={`sa-badge ${s.class}`}>{s.label}</span>;
}

// ── Modal: Crear Tenant ───────────────────────────────────────────────────────
function ModalCrearTenant({ onClose, onSuccess, planes }) {
    const [form, setForm] = useState({ name: "", subdomain: "", email: "", plan_id: "plan_basic", menu_template: "restaurante", owner_name: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return setError("El nombre es obligatorio.");
        setLoading(true);
        setError("");
        try {
            const res = await createTenant(form);
            onSuccess(res);
        } catch (err) {
            setError(err.response?.data?.detail || "Error al crear el restaurante.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="sa-modal" onClick={e => e.stopPropagation()}>
                <div className="sa-modal-header">
                    <h2 className="sa-modal-title">Nuevo Restaurante</h2>
                    <button className="sa-modal-close" onClick={onClose}><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="sa-modal-body space-y-4">
                    <div>
                        <label className="sa-label">Nombre del restaurante *</label>
                        <input
                            className="sa-input"
                            placeholder="Ej: Tacos El Fuego"
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            required
                        />
                    </div>
                    <div>
                        <label className="sa-label">Subdominio (se genera automático si se deja vacío)</label>
                        <input
                            className="sa-input"
                            placeholder="taco-fuego"
                            value={form.subdomain}
                            onChange={e => setForm(f => ({ ...f, subdomain: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                        />
                    </div>
                    <div>
                        <label className="sa-label">Email de contacto *</label>
                        <input
                            className="sa-input"
                            type="email"
                            placeholder="propietario@email.com"
                            value={form.email}
                            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                            required
                        />
                    </div>
                    <div>
                        <label className="sa-label">Nombre del propietario</label>
                        <input
                            className="sa-input"
                            placeholder="Ej: Juan Pérez"
                            value={form.owner_name}
                            onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="sa-label">Plantilla de Contenido / Branding</label>
                        <select
                            className="sa-input"
                            value={form.menu_template}
                            onChange={e => setForm(f => ({ ...f, menu_template: e.target.value }))}
                        >
                            <option value="restaurante">Restaurante Estándar (Índigo)</option>
                            <option value="pizzeria">Pizzería (Rojo/Naranja)</option>
                            <option value="bar">Bar / Pub (Dark Slate/Oro)</option>
                            <option value="taco_bar">Taco Bar (Amarillo/Verde)</option>
                        </select>
                    </div>
                    <div>
                        <label className="sa-label">Plan inicial</label>
                        <select
                            className="sa-input"
                            value={form.plan_id}
                            onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}
                        >
                            {planes.length === 0
                                ? <option value="plan_basic">Básico (plan por defecto)</option>
                                : planes.map(p => (
                                    <option key={p.plan_id} value={p.plan_id}>
                                        {p.name} — {p.price_monthly}€/mes
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                    {error && <div className="sa-error-box">{error}</div>}
                    <div className="sa-modal-footer">
                        <button type="button" className="sa-btn-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="sa-btn-primary" disabled={loading}>
                            {loading ? "Creando..." : "Crear Restaurante"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Modal: Cambiar Plan ───────────────────────────────────────────────────────
function ModalCambiarPlan({ tenant, planes, onClose, onSuccess }) {
    const [planId, setPlanId] = useState(tenant.subscription?.plan_id || "");
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        if (!planId) return;
        setLoading(true);
        try {
            await changeTenantPlan(tenant.company_id, planId);
            onSuccess();
        } catch (err) {
            alert(err.response?.data?.detail || "Error al cambiar plan");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="sa-modal" onClick={e => e.stopPropagation()}>
                <div className="sa-modal-header">
                    <h2 className="sa-modal-title">Cambiar Plan — {tenant.name}</h2>
                    <button className="sa-modal-close" onClick={onClose}><X size={20} /></button>
                </div>
                <div className="sa-modal-body space-y-4">
                    <p className="sa-modal-desc">Plan actual: <strong>{tenant.subscription?.plan_id || "Sin plan"}</strong></p>
                    <select className="sa-input" value={planId} onChange={e => setPlanId(e.target.value)}>
                        <option value="">Selecciona un plan…</option>
                        {planes.map(p => (
                            <option key={p.plan_id} value={p.plan_id}>
                                {p.name} — {p.price_monthly}€/mes
                            </option>
                        ))}
                    </select>
                    <div className="sa-modal-footer">
                        <button className="sa-btn-secondary" onClick={onClose}>Cancelar</button>
                        <button className="sa-btn-primary" onClick={handleSave} disabled={loading || !planId}>
                            {loading ? "Guardando..." : "Guardar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Modal: Confirmar Eliminación ──────────────────────────────────────────────
function ModalEliminar({ tenant, onClose, onSuccess }) {
    const [confirmText, setConfirmText] = useState("");
    const [loading, setLoading] = useState(false);
    const CONFIRM_WORD = "ELIMINAR";

    const handleDelete = async () => {
        if (confirmText !== CONFIRM_WORD) return;
        setLoading(true);
        try {
            await deleteTenant(tenant.company_id, true);
            onSuccess();
        } catch (err) {
            alert(err.response?.data?.detail || "Error al eliminar");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="sa-modal sa-modal-danger" onClick={e => e.stopPropagation()}>
                <div className="sa-modal-header">
                    <h2 className="sa-modal-title" style={{ color: 'var(--danger)' }}>
                        <AlertTriangle size={20} style={{ display: 'inline', marginRight: 8 }} />
                        Eliminar Restaurante
                    </h2>
                    <button className="sa-modal-close" onClick={onClose}><X size={20} /></button>
                </div>
                <div className="sa-modal-body space-y-4">
                    <p className="sa-modal-desc">
                        Esta acción es <strong>irreversible</strong>. Se eliminará el restaurante <strong>{tenant.name}</strong> y todos sus datos asociados.
                    </p>
                    <p className="sa-modal-desc">
                        Escribe <strong>{CONFIRM_WORD}</strong> para confirmar:
                    </p>
                    <input
                        className="sa-input"
                        placeholder={CONFIRM_WORD}
                        value={confirmText}
                        onChange={e => setConfirmText(e.target.value)}
                    />
                    <div className="sa-modal-footer">
                        <button className="sa-btn-secondary" onClick={onClose}>Cancelar</button>
                        <button
                            className="sa-btn-danger"
                            onClick={handleDelete}
                            disabled={confirmText !== CONFIRM_WORD || loading}
                        >
                            {loading ? "Eliminando..." : "Eliminar definitivamente"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function SuperAdminTenants() {
    const [data, setData] = useState(null);
    const [planes, setPlanes] = useState([]);
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);

    // Modales
    const [modalCrear, setModalCrear] = useState(false);
    const [modalPlan, setModalPlan] = useState(null);     // tenant seleccionado
    const [modalEliminar, setModalEliminar] = useState(null); // tenant seleccionado
    const [actionLoading, setActionLoading] = useState({});

    const load = useCallback(() => {
        setLoading(true);
        setError("");
        const params = { page, limit: 20 };
        if (search) params.q = search;
        if (filterStatus) params.status = filterStatus;

        fetchTenants(params)
            .then(setData)
            .catch((err) => {
                console.error("Error fetching tenants:", err);
                if (err.response?.status === 401 || err.response?.status === 403) {
                    setError("Sesión expirada. Por favor, inicia sesión nuevamente.");
                    // Redirect to login after short delay
                    setTimeout(() => window.location.href = "/superadmin/login", 2000);
                } else {
                    setError("No se pudo conectar con el servidor. Verifica que el backend esté activo.");
                }
            })
            .finally(() => setLoading(false));
    }, [page, search, filterStatus]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetchPlans().then(r => setPlanes(r.items || [])).catch(() => { });
    }, []);

    // Cambiar búsqueda resetea a página 1
    const handleSearch = (val) => { setSearch(val); setPage(1); };
    const handleStatus = (val) => { setFilterStatus(val); setPage(1); };

    const setAction = (id, state) => setActionLoading(prev => ({ ...prev, [id]: state }));

    const handleSuspend = async (tenant) => {
        if (!window.confirm(`¿Suspender "${tenant.name}"? El restaurante perderá acceso inmediatamente.`)) return;
        setAction(tenant.company_id, 'suspend');
        try {
            await suspendTenant(tenant.company_id, "Suspendido desde SuperAdmin");
            load();
        } catch (err) {
            alert(err.response?.data?.detail || "Error al suspender");
        } finally {
            setAction(tenant.company_id, null);
        }
    };

    const handleReactivate = async (tenant) => {
        setAction(tenant.company_id, 'reactivate');
        try {
            await reactivateTenant(tenant.company_id);
            load();
        } catch (err) {
            alert(err.response?.data?.detail || "Error al reactivar");
        } finally {
            setAction(tenant.company_id, null);
        }
    };

    const handleImpersonate = async (tenant) => {
        setAction(tenant.company_id, 'impersonate');
        try {
            const res = await impersonateTenant(tenant.company_id);
            const token = res.access_token;
            const subdomain = tenant.subdomain || tenant.company_id;

            let targetUrl = "";
            const hostname = window.location.hostname;
            
            if (hostname === "localhost" || hostname === "127.0.0.1") {
                targetUrl = `/auth/callback?token=${token}&tenant=${subdomain}&returnTo=/admin`;
            } else {
                const parts = hostname.split('.');
                const rootDomain = parts.length > 2 ? parts.slice(parts.length - 2).join('.') : hostname;
                targetUrl = `https://${subdomain}.${rootDomain}/auth/callback?token=${token}&returnTo=/admin`;
            }

            window.open(targetUrl, "_blank");
        } catch (err) {
            alert(err.response?.data?.detail || "No se pudo acceder al panel del restaurante");
        } finally {
            setAction(tenant.company_id, null);
        }
    };

    const items = data?.items || [];
    const total = data?.total || 0;
    const pages = data?.pages || 1;
    const isSuspended = (t) => t.subscription?.status === "paused" || t.subscription?.status === "cancelled" || t.status === "suspended";

    return (
        <SuperAdminLayout>
            <style>{`
                .sa-card {
                    background: rgba(15,23,42,0.6);
                    border: 1px solid rgba(255,255,255,0.07);
                    border-radius: 20px;
                    padding: 24px;
                    transition: border-color 0.2s, transform 0.2s;
                }
                .sa-card:hover { border-color: rgba(99,102,241,0.3); transform: translateY(-2px); }
                .sa-badge { font-size: 11px; font-weight: 800; padding: 3px 10px; border-radius: 20px; letter-spacing: 0.5px; text-transform: uppercase; }
                .badge-success { background: rgba(16,185,129,0.15); color: #10b981; border: 1px solid rgba(16,185,129,0.25); }
                .badge-info { background: rgba(59,130,246,0.15); color: #60a5fa; border: 1px solid rgba(59,130,246,0.25); }
                .badge-warning { background: rgba(245,158,11,0.15); color: #fbbf24; border: 1px solid rgba(245,158,11,0.25); }
                .badge-danger { background: rgba(239,68,68,0.15); color: #f87171; border: 1px solid rgba(239,68,68,0.25); }
                .badge-muted { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.4); border: 1px solid rgba(255,255,255,0.1); }
                .sa-btn-primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); color: white; border: none; border-radius: 12px; padding: 10px 20px; font-weight: 700; font-size: 13px; cursor: pointer; transition: opacity 0.2s, transform 0.1s; }
                .sa-btn-primary:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
                .sa-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
                .sa-btn-secondary { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.8); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 10px 20px; font-weight: 700; font-size: 13px; cursor: pointer; transition: background 0.2s; }
                .sa-btn-secondary:hover { background: rgba(255,255,255,0.1); }
                .sa-btn-ghost { background: transparent; color: rgba(255,255,255,0.5); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 7px 12px; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 6px; }
                .sa-btn-ghost:hover { background: rgba(255,255,255,0.06); color: white; border-color: rgba(255,255,255,0.15); }
                .sa-btn-ghost:disabled { opacity: 0.4; cursor: not-allowed; }
                .sa-btn-danger { background: rgba(239,68,68,0.15); color: #f87171; border: 1px solid rgba(239,68,68,0.3); border-radius: 12px; padding: 10px 20px; font-weight: 700; font-size: 13px; cursor: pointer; }
                .sa-btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
                .sa-input { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 12px 16px; color: white; font-size: 14px; outline: none; transition: border-color 0.2s; box-sizing: border-box; }
                .sa-input:focus { border-color: rgba(99,102,241,0.5); }
                .sa-label { display: block; font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
                .sa-error-box { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.25); color: #f87171; border-radius: 10px; padding: 12px 16px; font-size: 13px; font-weight: 600; }
                .sa-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 16px; text-align: center; }
                .sa-empty-icon { color: rgba(255,255,255,0.15); }
                .sa-empty-title { font-size: 18px; font-weight: 700; color: rgba(255,255,255,0.7); }
                .sa-empty-sub { font-size: 14px; color: rgba(255,255,255,0.35); max-width: 360px; }
                .space-y-2 > * + * { margin-top: 8px; }
                .space-y-4 > * + * { margin-top: 16px; }
                .sa-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(8px); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 24px; }
                .sa-modal { background: #0f172a; border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; width: 100%; max-width: 520px; box-shadow: 0 40px 100px rgba(0,0,0,0.5); }
                .sa-modal-danger { border-color: rgba(239,68,68,0.3); }
                .sa-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 24px 28px 0; }
                .sa-modal-title { font-size: 18px; font-weight: 800; color: white; }
                .sa-modal-close { background: transparent; border: none; cursor: pointer; color: rgba(255,255,255,0.4); padding: 4px; border-radius: 8px; transition: color 0.2s; }
                .sa-modal-close:hover { color: white; }
                .sa-modal-body { padding: 24px 28px; }
                .sa-modal-desc { font-size: 14px; color: rgba(255,255,255,0.55); line-height: 1.6; }
                .sa-modal-footer { display: flex; gap: 12px; justify-content: flex-end; padding-top: 8px; }
                .sa-pagination { display: flex; align-items: center; justify-content: center; gap: 12px; padding-top: 24px; }
                .sa-page-btn { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: rgba(255,255,255,0.7); border-radius: 10px; padding: 8px 16px; font-size: 13px; font-weight: 700; cursor: pointer; }
                .sa-page-btn:hover:not(:disabled) { background: rgba(99,102,241,0.15); border-color: rgba(99,102,241,0.3); color: white; }
                .sa-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }
                .sa-page-info { font-size: 13px; color: rgba(255,255,255,0.4); font-weight: 600; }
            `}</style>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ fontSize: 28, fontWeight: 900, color: 'white', letterSpacing: '-0.5px', marginBottom: 6 }}>
                        Directorio de Restaurantes
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15 }}>
                        Gestión y monitoreo de todos los clientes en Conexo POS
                        {!loading && <span style={{ marginLeft: 8, fontWeight: 700, color: 'rgba(99,102,241,0.8)' }}>— {total} total</span>}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="sa-btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <RefreshCw size={14} />Actualizar
                    </button>
                    <button className="sa-btn-primary" onClick={() => setModalCrear(true)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Plus size={16} />Nuevo Restaurante
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                    <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }} />
                    <input
                        className="sa-input"
                        type="search"
                        placeholder="Buscar por nombre, ID o subdominio…"
                        value={search}
                        onChange={e => handleSearch(e.target.value)}
                        style={{ paddingLeft: 42 }}
                    />
                </div>
                <div style={{ position: 'relative', minWidth: 180 }}>
                    <Filter size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
                    <select
                        className="sa-input"
                        value={filterStatus}
                        onChange={e => handleStatus(e.target.value)}
                        style={{ paddingLeft: 38, cursor: 'pointer', appearance: 'none' }}
                    >
                        <option value="">Todos los estados</option>
                        <option value="active">Activos</option>
                        <option value="trial">En Trial</option>
                        <option value="paused">Pausados</option>
                        <option value="cancelled">Cancelados</option>
                    </select>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="sa-error-box" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>⚠️ {error}</span>
                    <button className="sa-btn-ghost" onClick={load}><RefreshCw size={14} />Reintentar</button>
                </div>
            )}

            {/* Grid de tenants */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
                {loading
                    ? [1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)
                    : items.length === 0
                        ? <EmptyState search={search} onClear={() => { handleSearch(""); handleStatus(""); }} />
                        : items.map((tenant) => {
                            const sub = tenant.subscription;
                            const status = sub?.status || "inactive";
                            const isLoading = actionLoading[tenant.company_id];
                            const suspended = isSuspended(tenant);

                            return (
                                <div key={tenant.company_id} className="sa-card">
                                    {/* Cabecera */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                                        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                                            <div style={{
                                                width: 48, height: 48, borderRadius: 14,
                                                background: 'rgba(99,102,241,0.12)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                border: '1px solid rgba(99,102,241,0.2)'
                                            }}>
                                                <Store size={22} style={{ color: '#818cf8' }} />
                                            </div>
                                            <div style={{ overflow: 'hidden' }}>
                                                <h3 style={{ fontSize: 16, fontWeight: 800, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                                                    {tenant.name || "Restaurante"}
                                                </h3>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                                                    <Globe size={11} style={{ color: '#818cf8' }} />
                                                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>
                                                        {tenant.subdomain || tenant.company_id}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <StatusBadge status={status} />
                                    </div>

                                    {/* Info */}
                                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                            <span style={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                <CreditCard size={12} />Plan
                                            </span>
                                            <span style={{ color: 'white', fontWeight: 800 }}>
                                                {sub?.plan_id?.replace('plan_', '').toUpperCase() || "BÁSICO"}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                            <span style={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                <Calendar size={12} />Alta
                                            </span>
                                            <span style={{ color: 'white', fontWeight: 700 }}>
                                                {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString("es-ES") : "—"}
                                            </span>
                                        </div>
                                        {tenant.branches_count !== undefined && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 8 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Sucursales
                                                </span>
                                                <span style={{ color: 'white', fontWeight: 700 }}>{tenant.branches_count}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Acciones */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button
                                                className="sa-btn-ghost"
                                                style={{ flex: 1 }}
                                                onClick={() => handleImpersonate(tenant)}
                                                disabled={!!isLoading}
                                                title="Entrar como admin del restaurante"
                                            >
                                                <LogIn size={13} />
                                                {isLoading === 'impersonate' ? "Accediendo..." : "Entrar como Admin"}
                                            </button>
                                            <button
                                                className="sa-btn-ghost"
                                                onClick={() => setModalPlan(tenant)}
                                                disabled={!!isLoading}
                                                title="Cambiar plan"
                                            >
                                                <CreditCard size={13} />
                                            </button>
                                            {suspended ? (
                                                <button
                                                    className="sa-btn-ghost"
                                                    onClick={() => handleReactivate(tenant)}
                                                    disabled={!!isLoading}
                                                    title="Reactivar restaurante"
                                                    style={{ color: '#10b981' }}
                                                >
                                                    <PlayCircle size={13} />
                                                </button>
                                            ) : (
                                                <button
                                                    className="sa-btn-ghost"
                                                    onClick={() => handleSuspend(tenant)}
                                                    disabled={!!isLoading}
                                                    title="Suspender restaurante"
                                                    style={{ color: '#f59e0b' }}
                                                >
                                                    <Pause size={13} />
                                                </button>
                                            )}
                                            <button
                                                className="sa-btn-ghost"
                                                onClick={() => setModalEliminar(tenant)}
                                                disabled={!!isLoading}
                                                title="Eliminar restaurante"
                                                style={{ color: '#f87171' }}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                        <Link to={`/superadmin/tenants/${tenant.company_id}`} style={{ textDecoration: 'none' }}>
                                            <button className="sa-btn-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                                Gestionar Centro <ChevronRight size={15} />
                                            </button>
                                        </Link>
                                    </div>
                                </div>
                            );
                        })
                }
            </div>

            {/* Paginación */}
            {!loading && pages > 1 && (
                <div className="sa-pagination">
                    <button className="sa-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                        ← Anterior
                    </button>
                    <span className="sa-page-info">Página {page} de {pages}</span>
                    <button className="sa-page-btn" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>
                        Siguiente →
                    </button>
                </div>
            )}

            {/* Modales */}
            {modalCrear && (
                <ModalCrearTenant
                    planes={planes}
                    onClose={() => setModalCrear(false)}
                    onSuccess={() => { setModalCrear(false); load(); }}
                />
            )}
            {modalPlan && (
                <ModalCambiarPlan
                    tenant={modalPlan}
                    planes={planes}
                    onClose={() => setModalPlan(null)}
                    onSuccess={() => { setModalPlan(null); load(); }}
                />
            )}
            {modalEliminar && (
                <ModalEliminar
                    tenant={modalEliminar}
                    onClose={() => setModalEliminar(null)}
                    onSuccess={() => { setModalEliminar(null); load(); }}
                />
            )}
        </SuperAdminLayout>
    );
}
